import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Input } from '@/components/ui/input';
import { MapPin, Plus, CheckCircle2 } from 'lucide-react';
import { API } from '@/App';

/**
 * Autocomplete for enroute-stop names inside the day-card editor.
 * As the operator types, we hit /api/pollen/locations/search-stops and
 * surface the top 12 saved enroute stops. Picking a row hydrates the
 * stop with the canonical coords, google_maps_url and notes — but
 * notes are kept editable per-tour (first-write-wins on the database;
 * later edits live on the tour record only).
 */
const StopNameAutocomplete = ({ value, dayIndex, index, onPick, onChangeName }) => {
  const [q, setQ] = useState(value || '');
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState([]);
  const wrapRef = useRef(null);

  // Keep parent's value in sync when typing in.
  useEffect(() => { setQ(value || ''); }, [value]);

  // Close on outside click
  useEffect(() => {
    const onClick = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  // Debounced fetch
  useEffect(() => {
    const s = (q || '').trim();
    if (!s || s.length < 2) { setResults([]); return undefined; }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const r = await fetch(`${API}/pollen/locations/search-stops?q=${encodeURIComponent(s)}`, { credentials: 'include' });
        if (!r.ok) return;
        const data = await r.json();
        if (!cancelled) setResults(Array.isArray(data) ? data : []);
      } catch { /* network blip — silent */ }
    }, 200);
    return () => { cancelled = true; clearTimeout(t); };
  }, [q]);

  const exactMatch = useMemo(
    () => results.some(r => (r.name || '').toLowerCase() === (q || '').trim().toLowerCase()),
    [results, q]
  );

  const handleSelect = (r) => {
    onPick({
      name: r.name || '',
      lat: typeof r.lat === 'number' ? r.lat : null,
      lng: typeof r.lng === 'number' ? r.lng : null,
      google_maps_url: r.google_maps_url || '',
      notes: r.notes || '',
      // Subtype maps to the type dropdown; keep the existing if blank.
      type: r.subtype || undefined,
    });
    setOpen(false);
  };

  return (
    <div ref={wrapRef} className="relative flex-1">
      <Input
        placeholder="Stop name — search or type new"
        value={q}
        onChange={(e) => { setQ(e.target.value); onChangeName(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        className="h-7 text-xs"
        data-testid={`stop-name-${dayIndex}-${index}`}
      />
      {open && q && q.trim().length >= 2 && (results.length > 0 || !exactMatch) && (
        <div className="absolute z-50 left-0 right-0 mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {results.map(r => (
            <button
              key={r.id}
              type="button"
              className="w-full text-left px-2.5 py-1.5 hover:bg-muted text-xs flex items-center gap-2"
              onMouseDown={(e) => { e.preventDefault(); handleSelect(r); }}
              data-testid={`stop-suggest-${r.id}`}
            >
              <MapPin className="w-3 h-3 text-amber-600 shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="font-medium truncate">{r.name}</div>
                {(r.subtype || r.usage_count) && (
                  <div className="text-[10px] text-muted-foreground truncate">
                    {r.subtype || 'POI'}{r.usage_count ? ` · used ${r.usage_count}×` : ''}
                  </div>
                )}
              </div>
              {(r.lat != null && r.lng != null) && (
                <CheckCircle2 className="w-3 h-3 text-emerald-500 shrink-0" title="Pinned" />
              )}
            </button>
          ))}
          {!exactMatch && (
            <button
              type="button"
              className="w-full text-left px-2.5 py-1.5 hover:bg-emerald-50 text-xs flex items-center gap-2 border-t"
              onMouseDown={(e) => { e.preventDefault(); setOpen(false); }}
              data-testid={`stop-create-new-${dayIndex}-${index}`}
            >
              <Plus className="w-3 h-3 text-emerald-600 shrink-0" />
              Create new stop: <span className="font-medium ml-0.5">"{q.trim()}"</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default StopNameAutocomplete;
